import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTransferClasses } from './hooks/useTransferClasses'
import BruktbilfinansieringInfoBox from './BruktbilfinansieringInfoBox'
import type { BruktbilfinansieringFilters } from './hooks/useBruktbilfinansiering'
import type { TransferClassRow } from '../../types/bruktbilfinansiering'
import { ALL_SCOPE1_CLASSES, SCOPE1_CLASS_GROUPS } from '../../types/bruktbilfinansiering'

interface Props {
  bankOrgNr: string
  bankName: string
  filters: BruktbilfinansieringFilters
}

type SortKey = keyof Pick<TransferClassRow,
  'class_label' | 'total_transfers' | 'loan_ratio_pct' | 'bank_loan_count' |
  'bank_total_belop' | 'bank_avg_belop' |
  'other_bank_loan_count' | 'other_bank_total_belop' |
  'bank_market_share_count_pct' | 'bank_market_share_volume_pct'
>

// Plain Norwegian number, no "kr" suffix. For Total/Øvrige volum / Snittbeløp columns
// per 2026-05-12 feedback "fjerne prosent og kroner".
function fmtVolum(val: number | null): string {
  if (val === null || val === undefined) return '–'
  return new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(val)
}

function fmtPct(val: number): string {
  return `${val.toFixed(1)} %`
}

const ALL_GROUPS = ['BILBRANSJE', 'PERSON', 'FIRMA', 'BILUTLEIE', 'LEASING', 'FORSIKRING'] as const

const COLUMN_TOOLTIP_KEYS = {
  class_label: 'klasser.tooltips.classLabel',
  total_transfers: 'klasser.tooltips.totalTransfers',
  loan_ratio_pct: 'klasser.tooltips.loanRatioPct',
  bank_loan_count: 'klasser.tooltips.bankLoanCount',
  bank_total_belop: 'klasser.tooltips.bankTotalBelop',
  bank_avg_belop: 'klasser.tooltips.bankAvgBelop',
  other_bank_loan_count: 'klasser.tooltips.otherBankLoanCount',
  other_bank_total_belop: 'klasser.tooltips.otherBankTotalBelop',
  bank_market_share_count_pct: 'klasser.tooltips.bankMarketShareCountPct',
  bank_market_share_volume_pct: 'klasser.tooltips.bankMarketShareVolumePct',
} as const satisfies Record<SortKey, string>

export default function KlasserTab({ bankOrgNr, bankName, filters }: Props) {
  const { t } = useTranslation('bbf')
  const [selectedClasses, setSelectedClasses] = useState<string[]>([...ALL_SCOPE1_CLASSES])
  const [sortKey, setSortKey] = useState<SortKey>('total_transfers')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const navigate = useNavigate()

  function openClassDetails(classLabel: string) {
    const sp = new URLSearchParams()
    sp.set('start_date', filters.startDate)
    sp.set('end_date', filters.endDate)
    if (filters.varebil !== undefined) sp.set('varebil', String(filters.varebil))
    if (filters.buyerPostnummer) sp.set('buyer_postnummer', filters.buyerPostnummer)
    if (filters.dealerPostnummer) sp.set('dealer_postnummer', filters.dealerPostnummer)
    navigate(`/bruktbil-finansiering/${bankOrgNr}/klasser/${encodeURIComponent(classLabel)}?${sp.toString()}`)
  }

  const { data, loading, error } = useTransferClasses(bankOrgNr, filters, selectedClasses)

  function toggleClass(cls: string) {
    setSelectedClasses(prev =>
      prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
    )
  }

  function selectGroup(group: string) {
    const groupClasses = SCOPE1_CLASS_GROUPS[group] as string[]
    const allSelected = groupClasses.every(c => selectedClasses.includes(c))
    if (allSelected) {
      setSelectedClasses(prev => prev.filter(c => !groupClasses.includes(c)))
    } else {
      setSelectedClasses(prev => [...new Set([...prev, ...groupClasses])])
    }
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      // String columns default to ascending (alphabetical A→Z); numeric to descending (largest first).
      setSortDir(key === 'class_label' ? 'asc' : 'desc')
    }
  }

  const sortedRows = [...(data?.rows ?? [])].sort((a, b) => {
    const va = a[sortKey] ?? 0
    const vb = b[sortKey] ?? 0
    if (typeof va === 'string' || typeof vb === 'string') {
      const sa = String(va)
      const sb = String(vb)
      return sortDir === 'asc' ? sa.localeCompare(sb, 'nb') : sb.localeCompare(sa, 'nb')
    }
    return sortDir === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number)
  })

  function SortHeader({ col, label }: { col: SortKey; label: string }) {
    const active = sortKey === col
    return (
      <th
        onClick={() => handleSort(col)}
        className="px-3 py-2 text-right cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700"
        title={t(COLUMN_TOOLTIP_KEYS[col])}
      >
        <span className="flex items-center justify-end gap-1">
          {label}
          {active ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ' ↕'}
          <span className="text-gray-400 dark:text-gray-500 text-xs ml-1" title={t(COLUMN_TOOLTIP_KEYS[col])}>?</span>
        </span>
      </th>
    )
  }

  return (
    <div className="space-y-4">
      <BruktbilfinansieringInfoBox bankName={bankName} filters={filters} tab="klasser" overrideClasses={selectedClasses} />

      {/* KPI strip */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('klasser.totalTransfers')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {data.kpis.total_transfers.toLocaleString('nb-NO')}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('shared.loanShare')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {fmtPct(data.kpis.overall_loan_ratio_pct)}
            </p>
          </div>
          <div
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            title={t('klasser.shareCountTitle')}
          >
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('shared.marketShareCount')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {fmtPct(data.kpis.overall_bank_market_share_count_pct)}
            </p>
          </div>
          <div
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            title={t('klasser.shareVolumeTitle')}
          >
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('shared.marketShareVolume')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {fmtPct(data.kpis.overall_bank_market_share_volume_pct)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('shared.totalVolume')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {fmtVolum(data.kpis.total_bank_belop)}
            </p>
          </div>
        </div>
      )}

      {/* Multiselect */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-1">{t('shared.classesLabel')}</span>
          <button
            onClick={() => setSelectedClasses([...ALL_SCOPE1_CLASSES])}
            className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          >
            {t('shared.all')}
          </button>
          {ALL_GROUPS.map(group => (
            <button
              key={group}
              onClick={() => setSelectedClasses([...SCOPE1_CLASS_GROUPS[group] as string[]])}
              className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            >
              {group}
            </button>
          ))}
          <button
            onClick={() => setSelectedClasses([])}
            className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          >
            {t('shared.clearAll')}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {Object.entries(SCOPE1_CLASS_GROUPS).map(([group, classes]) => (
            <div key={group}>
              <button
                onClick={() => selectGroup(group)}
                className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 hover:underline"
              >
                {group}
              </button>
              {(classes as string[]).map(cls => (
                <label key={cls} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedClasses.includes(cls)}
                    onChange={() => toggleClass(cls)}
                    className="accent-primary-600"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    {cls.replace(`${group}_TO_`, '')}
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading && <p className="text-sm text-gray-500 dark:text-gray-400">{t('shared.loading')}</p>}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {!loading && !error && data && (
        <div className="overflow-x-auto card p-0">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('class_label')}
                  className="text-left cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-800"
                  title={t(COLUMN_TOOLTIP_KEYS['class_label'])}
                >
                  <span className="flex items-center gap-1">
                    {t('klasser.class')}
                    {sortKey === 'class_label' ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ' ↕'}
                  </span>
                </th>
                <SortHeader col="total_transfers" label={t('klasser.transfers')} />
                <SortHeader col="loan_ratio_pct" label={t('shared.loanShare')} />
                <SortHeader col="bank_loan_count" label={t('shared.bankLoans')} />
                <SortHeader col="other_bank_loan_count" label={t('klasser.otherLoans')} />
                <SortHeader col="bank_market_share_count_pct" label={t('shared.marketShareCount')} />
                <SortHeader col="bank_total_belop" label={t('shared.totalVolume')} />
                <SortHeader col="other_bank_total_belop" label={t('klasser.otherVolume')} />
                <SortHeader col="bank_market_share_volume_pct" label={t('shared.marketShareVolume')} />
                <SortHeader col="bank_avg_belop" label={t('shared.avgAmount')} />
              </tr>
            </thead>
            <tbody>
              {sortedRows.length > 0 && (() => {
                const t = sortedRows.reduce((acc, r) => ({
                  total_transfers: acc.total_transfers + r.total_transfers,
                  bank_loan_count: acc.bank_loan_count + r.bank_loan_count,
                  other_bank_loan_count: acc.other_bank_loan_count + r.other_bank_loan_count,
                  bank_total_belop: acc.bank_total_belop + Number(r.bank_total_belop ?? 0),
                  other_bank_total_belop: acc.other_bank_total_belop + Number(r.other_bank_total_belop ?? 0),
                  loan_count_for_ratio: acc.loan_count_for_ratio + Math.round(r.total_transfers * r.loan_ratio_pct / 100),
                }), {
                  total_transfers: 0, bank_loan_count: 0, other_bank_loan_count: 0,
                  bank_total_belop: 0, other_bank_total_belop: 0, loan_count_for_ratio: 0,
                })
                const loanRatio = t.total_transfers > 0 ? (t.loan_count_for_ratio / t.total_transfers) * 100 : 0
                const knownBankLoans = t.bank_loan_count + t.other_bank_loan_count
                const shareCount = knownBankLoans > 0 ? (t.bank_loan_count / knownBankLoans) * 100 : 0
                const knownBankVolum = t.bank_total_belop + t.other_bank_total_belop
                const shareVolum = knownBankVolum > 0 ? (t.bank_total_belop / knownBankVolum) * 100 : 0
                const avgBelop = t.bank_loan_count > 0 ? t.bank_total_belop / t.bank_loan_count : 0
                return (
                  <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                    <td>∑ TOTAL</td>
                    <td className="text-right tabular-nums">{t.total_transfers.toLocaleString('nb-NO')}</td>
                    <td className="text-right tabular-nums">{loanRatio.toFixed(1)}</td>
                    <td className="text-right tabular-nums">{t.bank_loan_count.toLocaleString('nb-NO')}</td>
                    <td className="text-right tabular-nums">{t.other_bank_loan_count.toLocaleString('nb-NO')}</td>
                    <td className="text-right tabular-nums">{shareCount.toFixed(1)}</td>
                    <td className="text-right tabular-nums">{fmtVolum(t.bank_total_belop)}</td>
                    <td className="text-right tabular-nums">{fmtVolum(t.other_bank_total_belop)}</td>
                    <td className="text-right tabular-nums">{shareVolum.toFixed(1)}</td>
                    <td className="text-right tabular-nums">{fmtVolum(avgBelop)}</td>
                  </tr>
                )
              })()}
              {sortedRows.map((row, idx) => (
                <tr
                  key={row.class_label}
                  onClick={() => openClassDetails(row.class_label)}
                  className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900/40' : 'bg-gray-50 dark:bg-gray-800/60'}`}
                >
                  <td className="font-mono text-xs">
                    <span className="text-primary-600 dark:text-primary-400 hover:underline">
                      {row.class_label}
                    </span>
                  </td>
                  <td className="text-right tabular-nums">{row.total_transfers.toLocaleString('nb-NO')}</td>
                  <td className="text-right tabular-nums">{row.loan_ratio_pct.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{row.bank_loan_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right tabular-nums">{row.other_bank_loan_count.toLocaleString('nb-NO')}</td>
                  <td className="text-right tabular-nums">{row.bank_market_share_count_pct.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{fmtVolum(row.bank_total_belop)}</td>
                  <td className="text-right tabular-nums">{fmtVolum(row.other_bank_total_belop)}</td>
                  <td className="text-right tabular-nums">{row.bank_market_share_volume_pct.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{fmtVolum(row.bank_avg_belop)}</td>
                </tr>
              ))}
              {sortedRows.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-gray-500 dark:text-gray-400 py-6">
                    {t('klasser.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
